const leftPad = require('left-pad');
const parseISODuration = require('parse-iso-duration');
const { getZonedTime, findTimeZone } = require('timezone-support');
const { formatZonedTime } = require('timezone-support/dist/parse-format');
const jstz = require('jstz');

// Use AP style for months on timestamps, per News editorial request:
// Abbreviate only the long months: Jan., Feb., Aug., Sept., Oct., Nov., Dec.
// The rest of the months are spelled out: March, April, May, June, July
const AP_MONTH_MAP = {
  1: 'Jan.',
  2: 'Feb.',
  3: 'March',
  4: 'April',
  5: 'May',
  6: 'June',
  7: 'July',
  8: 'Aug.',
  9: 'Sept.',
  10: 'Oct.',
  11: 'Nov.',
  12: 'Dec.',
};
const AP_MONTHS = Object.values(AP_MONTH_MAP);

const MONTH_MAP = {
  1: 'January',
  2: 'February',
  3: 'March',
  4: 'April',
  5: 'May',
  6: 'June',
  7: 'July',
  8: 'August',
  9: 'September',
  10: 'October',
  11: 'November',
  12: 'December',
};
const MONTHS = Object.values(MONTH_MAP);

const DAY_MAP = {
  0: 'Sunday',
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
};
const DAYS = Object.values(DAY_MAP);

const pad = (number) => leftPad(String(number), 2, '0');

const getSecondsFromMS = (ms) => Math.floor((ms / 1000) % 60);

const getMinutesFromMS = (ms) => Math.floor((ms / (1000 * 60)) % 60);

const getHoursFromMS = (ms) => Math.floor((ms / (1000 * 60 * 60)) % 24);

// Finds a token within the format string
const findToken = (arr, t) => arr.findIndex((i) => i.includes(t));

/**
 * @returns {String}
 */
const getRuntimeIANATimeZone = () => {
  const timeZoneInstance = jstz.determine();
  const timeZoneName = timeZoneInstance.name();

  return timeZoneName;
};

/**
 * Format date using IANA time zone and customizable format string
 * @param {String} dateString
 * @param {String} timeZone iana time zone
 * @returns {Object} date object with { year, month, day, hours, minutes, ... }
 */
const parseDate = (dateString, timeZone) => {
  if (!timeZone) {
    timeZone = getRuntimeIANATimeZone(); // eslint-disable-line no-param-reassign
  }

  const date = new Date(dateString);
  const canonicalTimeZone = findTimeZone(timeZone);
  return getZonedTime(date, canonicalTimeZone);
};

// Fallback if translation object is not passed to date function
const langaugeStub = {
  t: (key) => key,
};

/**
 * Format date in AP style
 * @param {String} dateString
 * @param {String} timeZone iana time zone
 * @returns {String}
 * NOTE: the format string does not allow extra characters like commas and delimiters.
 */
const getFormattedDateString = (dateString, timeZone, options, language = langaugeStub) => {
  const {
    format = 'M D YYYY',
    useAP = true,
    commaAfterDay = true,
    commaAfterDayOfWeek = true,
  } = options || {};

  const formatTokens = format.split(' ');

  const parsedDate = parseDate(dateString, timeZone, format);
  const digitFormattedDate = formatZonedTime(parsedDate, format);
  const formattedDateTokens = digitFormattedDate.split(' ');

  // Replace month with proper format
  const monthIndex = findToken(formatTokens, 'M');
  if (monthIndex !== -1) {
    const monthDigit = formattedDateTokens[monthIndex];
    const monthName = (useAP ? AP_MONTH_MAP : MONTH_MAP)[monthDigit];
    formattedDateTokens[monthIndex] = language.t(monthName, { keySeparator: false });
  }

  // Replace day of week with proper format
  const dayOfWeekIndex = findToken(formatTokens, 'd');
  if (dayOfWeekIndex !== -1) {
    const dayOfWeekDigit = formattedDateTokens[dayOfWeekIndex];
    const dayOfWeekName = language.t(DAY_MAP[dayOfWeekDigit], { keySeparator: false });
    formattedDateTokens[dayOfWeekIndex] = `${dayOfWeekName}${commaAfterDayOfWeek ? ',' : ''}`;
  }

  // Replace day of month with proper format
  const dayIndex = findToken(formatTokens, 'D');
  if (dayIndex !== -1) {
    const dayDigit = formattedDateTokens[dayIndex];
    formattedDateTokens[dayIndex] = `${dayDigit}${commaAfterDay ? ',' : ''}`;
  }

  return formattedDateTokens.join(' ');
};

/**
 * @param {String} dateString
 * @param {String} timeZone iana time zone
 * @returns {String}
 */
const getFormattedTimeString = (dateString, timeZone) => (
  formatZonedTime(parseDate(dateString, timeZone), 'h:mm A z')
);

/**
 * @param {String} dateString
 * @param {String} timeZone iana time zone
 * @param {String} delimiter
 * @returns {String}
 */
const getFormattedDateTimeString = (dateString, timeZone, delimiter = ' / ', language) => {
  const formattedDateString = getFormattedDateString(dateString, timeZone, {}, language);
  const formattedTimeString = getFormattedTimeString(dateString, timeZone);

  return `${formattedDateString}${delimiter}${formattedTimeString}`;
};

/**
 * @param {String} dateString
 * @param {String} timeZone iana time zone
 * @returns {String}
 */
const getFormattedMonthAndDayString = (dateString, timeZone, language) => getFormattedDateString(
  dateString,
  timeZone,
  {
    format: 'M D',
    commaAfterDay: false,
  },
  language,
);

/**
 * @param {String} dateString
 * @param {String} timeZone iana time zone
 * @returns {String}
 */
const getFormattedDateYearString = (dateString, timeZone) => getFormattedDateString(
  dateString,
  timeZone,
  { format: 'YYYY' },
);

/**
 * Calculate time ellapsed since date in date units
 * @param {String} dateString
 */
const timeFrom = (dateString) => {
  const date = new Date(dateString);
  const millisec = Math.abs(Date.now() - date.getTime());
  const minutes = (millisec / (1000 * 60)).toFixed(0);
  const hours = (millisec / (1000 * 60 * 60)).toFixed(0);
  const days = (millisec / (1000 * 60 * 60 * 24)).toFixed(0);

  if (minutes < 60) {
    return `${minutes}m`;
  } if (hours < 24) {
    return `${hours}h`;
  }

  return `${days}d`;
};

/**
 * Calculate and format duration
 * @param {*} duration
 */
const formatDuration = (duration) => {
  const ms = typeof duration === 'string'
    ? parseISODuration(duration)
    : duration;

  if (!ms) {
    return null;
  }

  const hours = getHoursFromMS(ms);
  const minutes = getMinutesFromMS(ms);
  const seconds = getSecondsFromMS(ms);

  if (!hours) {
    return `${pad(minutes)}:${pad(seconds)}`;
  }

  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
};

/**
 * @param {*} d
 * @param {*} offset
 */
const addDays = (d, offset) => {
  const date = new Date(d.valueOf());
  date.setDate(date.getDate() + offset);
  return date;
};

/**
 * @param {String} dateString
 */
const formatAsISO = (dateString) => {
  const date = new Date(dateString);
  if (Number.isNaN(Date.parse(date))) {
    return '';
  }
  return date.toISOString();
};

/**
 * @param {Object} dateA Date object || millisecond int
 * @param {Object} dateB Date object || millisecond int
 */
const getHourDifference = (dateA, dateB) => (
  Math.abs(dateA - dateB) / 36e5
);

/**
 * Is the given unix timestamp older than now.
 * @param {String} timestamp
 */
const hasExpired = (timestamp) => new Date(parseInt(timestamp, 10)) < new Date();

/**
 * @param {Object} dateA Date object || millisecond int
 * @param {Object} dateB Date object || millisecond int
 */
const isBefore = (datetime, maxMilliSeconds) => {
  const date = new Date(datetime);
  const millisec = Math.abs(Date.now() - date.getTime());

  if (millisec < maxMilliSeconds) {
    return true;
  }

  return false;
};

/**
 * @param {String} dateTime
 * @returns {Object}
 */
const getDateTime = (dateTime) => new Date(dateTime);

module.exports = {
  // constants
  AP_MONTH_MAP,
  AP_MONTHS,
  MONTH_MAP,
  MONTHS,
  DAY_MAP,
  DAYS,
  // functions
  addDays,
  formatAsISO,
  formatDuration,
  getDateTime,
  getFormattedDateString,
  getFormattedDateTimeString,
  getFormattedDateYearString,
  getFormattedMonthAndDayString,
  getFormattedTimeString,
  getHourDifference,
  getRuntimeIANATimeZone,
  hasExpired,
  isBefore,
  parseDate,
  timeFrom,
};
